/**
 * FLAGS — guard de ruta/página por feature.
 *
 * `<FeatureGate feature="billing.core">…</FeatureGate>` muestra el contenido
 * solo si la feature está efectiva-habilitada en el contexto (org activa por
 * defecto, curso opcional). Deshabilitada ⇒ estado amable «deshabilitada por
 * tu academia» (no un 404): explica qué pasa, que los DATOS SE CONSERVAN y
 * enlaza a la guía del centro de ayuda cuando existe.
 *
 * SOLO UX — la autoridad es el server (`feature_gate` → 403 estructurado).
 */
import { type Accessor, type Component, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ToggleLeft } from "lucide-solid";
import { featureGraph } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@/lib/u-router";
import { useFeatureEnabled } from "./use-feature-enabled";

/** Estado premium «feature deshabilitada por tu academia». */
const FeatureDisabledState: Component<{
  /** Key de la feature pedida (para el nombre legible del manifest). */
  feature: string;
  /** Key culpable (un requisito del grafo) si difiere de la pedida. */
  blockedBy?: string;
  class?: string;
}> = (props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const graph = featureGraph();
  const label = (key: string) => graph.get(key)?.name ?? key;
  const description = () => {
    const featureName = label(props.feature);
    if (props.blockedBy && props.blockedBy !== props.feature) {
      const blockerName = label(props.blockedBy);
      return t(
        msg`"${featureName}" needs "${blockerName}", which your academy has turned off. Nothing was deleted: everything comes back when it's re-enabled.`,
      );
    }
    return t(
      msg`Your academy has turned off "${featureName}". Nothing was deleted: your data is preserved and everything comes back when it's re-enabled.`,
    );
  };

  return (
    <div data-testid="feature-disabled-state" class={props.class ?? "mx-auto max-w-2xl px-4 py-16"}>
      <EmptyState
        icon={ToggleLeft}
        title={t(msg`Disabled by your academy`)}
        description={description()}
      />
      <div class="mt-4 flex justify-center">
        <Button
          variant="outline"
          data-testid="feature-disabled-home"
          onClick={() => {
            navigate({ to: "/learn" });
          }}
        >
          {t(msg`Go to Learn`)}
        </Button>
      </div>
    </div>
  );
};

/**
 * HOC para rutas: `component: withFeatureGate("billing.core", FinancePage)`.
 * La ruta sigue existiendo (no 404); su contenido se sustituye por el estado
 * «deshabilitada por tu academia» cuando la feature está apagada.
 */
export function withFeatureGate(feature: string, Comp: Component): Component {
  return () => (
    <FeatureGate feature={feature}>
      <Comp />
    </FeatureGate>
  );
}

/** Envuelve el contenido de una página gateada por feature. */
export const FeatureGate: Component<{
  feature: string;
  orgId?: Accessor<string | undefined>;
  courseId?: Accessor<string | undefined>;
  children: JSX.Element;
}> = (props) => {
  const state = useFeatureEnabled(() => props.feature, {
    orgId: () => props.orgId?.(),
    courseId: () => props.courseId?.(),
  });
  return (
    <Show
      when={state().enabled}
      fallback={<FeatureDisabledState feature={props.feature} blockedBy={state().blockedBy} />}
    >
      {props.children}
    </Show>
  );
};
